<template>
    <div class="flex flex-col gap-1" :class="placement === 'sidebar' ? 'w-full' : 'w-screen md:w-full md:max-w-(--breakpoint-md) md:mx-auto shrink-0 mb-2 px-2 items-center'">
        <BaseButton
            theme="secondary"
            :class="{ 'w-full': placement === 'sidebar' }"
            data-testid="push-goals-to-workday"
            :disabled="draftLoading"
            class="flex items-center justify-center gap-2"
            @click="handleClick">
            <i class="bi bi-bullseye" aria-hidden="true"></i>
            {{ t("chat.goalPush.draftButton") }}
        </BaseButton>

        <ChatGoalPushDialog
            v-model:open="dialogOpen"
            :draft="draft"
            :editing-existing="editingExisting"
            :category-options="categoryOptions"
            :org-goal-options="orgGoalOptions"
            :draft-error="draftError"
            :message-id="latestMessageId" />
    </div>
</template>

<script setup lang="ts">
import { ChatAction } from "~vue/chatActions";
import BaseButton from "~vue/components/BaseButton.vue";
import { useActionHandler } from "~vue/composables/useActionHandler";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import { resolveActionRefusal } from "~vue/utils/actionRefusal";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { inject, ref } from "vue";
import { useI18n } from "vue-i18n";

import ChatGoalPushDialog from "./ChatGoalPushDialog.vue";

interface DraftGoal {
    goal_id: string;
    name: string;
    description: string;
    due_date: string;
    goal_category_id: string;
    organization_goal_id: string;
}
interface GoalPushDraft {
    goals: DraftGoal[];
}
interface SelectOption {
    value: string;
    label: string;
}

interface Props {
    latestMessageId: number;
    /** ``sidebar`` fills the performance chat's right-hand panel; ``chat`` sits above the composer. */
    placement?: "chat" | "sidebar";
}

const props = withDefaults(defineProps<Props>(), { placement: "chat" });

const { t } = useI18n();
const { execute } = useActionHandler();
const coachingSessionUuid = inject<string | undefined>("coachingSessionUuid", undefined);

const dialogOpen = ref(false);
const draft = ref<GoalPushDraft | null>(null);
const editingExisting = ref(false);
const categoryOptions = ref<SelectOption[]>([]);
const orgGoalOptions = ref<SelectOption[]>([]);
const draftLoading = ref(false);
const draftError = ref("");

// Open the dialog immediately with a loading state, then draft in the background: the extraction
// (an LLM call, plus the org-goal fetch) takes a couple of seconds, so we don't block the open on it.
async function handleClick() {
    draft.value = null;
    editingExisting.value = false;
    categoryOptions.value = [];
    orgGoalOptions.value = [];
    draftError.value = "";
    draftLoading.value = true;
    dialogOpen.value = true;
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}push_goals_clicked`, { session_id: coachingSessionUuid }, coachingSessionUuid);

    try {
        const result = await resolveActionRefusal(execute(ChatAction.DRAFT_GOAL_PUSH_CARD, { message_id: props.latestMessageId }, { sync: true }));
        if (result?.success && result.payload?.draft) {
            draft.value = result.payload.draft;
            editingExisting.value = Boolean(result.payload?.editing_existing);
            categoryOptions.value = result.payload?.category_options ?? [];
            orgGoalOptions.value = result.payload?.org_goal_options ?? [];
        } else {
            draftError.value = result?.payload?.error ?? t("chat.goalPush.draftError");
        }
    } catch (e) {
        logError(e);
        draftError.value = t("chat.goalPush.draftError");
    } finally {
        draftLoading.value = false;
    }
}
</script>
